import React, { useEffect } from "react";
import { BreadcrumbItem, Nav, NavItem, NavbarText } from "reactstrap";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import { Mutation, Button } from "@repoflow/components/section";
import * as Preview from "@repoflow/components/preview";
import { Provider as EditorProvider } from "@repoflow/components/editor";
import { Layout } from "@repoflow/components/layout-dual";
import { Content as ContentAvailable } from "@repoflow/components/content-available";
import { useNavbarContext } from "@repoflow/components/navbar";
import { useSidebarContext } from "@repoflow/components/sidebar-menu";
import ContentTabs, { useContentState } from "@repoflow/components/content-tabs";
import { Form as TypesForm } from "@repoflow/components/types-form";
import { ValuesPane, typesHelp } from "components/types-help";
import * as widgets from "widgets/register";
import * as SectionQL from "queries/section";
import SectionPublicSchema from "@common/schema/section/public";
import TypesSchema from "@common/schema/types";
import { types as paramTypes } from "@common/schema/types/params/utils";

import { Frame } from "./frame";

export const Sidebar = () => null;

export const Indice = () => (
  <>
    <BreadcrumbItem>Edit</BreadcrumbItem>
  </>
);

// The field lists, hoisted OUT of the components.
//
// A `config` object literal in JSX is a new object on every render, and it is
// handed to the form's SchemaProvider, which rebuilds the widget tree
// underneath it. Every keystroke re-renders this component, so an inline config
// means the control being typed into is detached and replaced between
// keystrokes: the DOM ends up holding the text, React state does not, and the
// save writes the value the section already had. It reads as a save that
// worked — it navigates, it reports no error — against a screen that faithfully
// shows the old content.
//
// Module scope rather than useMemo because none of these depend on anything.
const CODE_FIELDS = {
  "/Public/Section/Update": {
    fields: [
      { property: "html", layout: "wide" },
      { property: "style", layout: "wide" },
      { property: "script", layout: "wide" },
    ],
  },
};

const DETAILS_FIELDS = {
  "/Public/Section/Update": {
    fields: [
      "tags",
      { property: "frame", layout: "wide" },
    ],
  },
};

// The section editor: what the section is made of on the left, what it renders
// as on the right, and one Save for both.
//
// It is the PAGE editor's screen, and that is the point of it — a section is
// markup with slots in it, and markup written against a form with no picture
// beside it is written blind. The two share the frame, the protocol, the
// composer and the layout; ../preview is the whole of the adaptation, and it is
// twenty lines. See ../../../pages/page/edit, which this follows in every
// structural decision:
//
//   One mutation, one Save, one value object. A section is written whole, and
//   splitting the save would let somebody leave with the markup on the Code tab
//   saved and the type it reads from the Data tab not. Save therefore lives in
//   the tab strip rather than under any one form, and the dirty-tracking Button
//   reflects the section, not the tab.
//
//   Saving does NOT navigate. It used to return to the section's own screen,
//   which was reasonable for a form and is not for an editor: you save several
//   times in one sitting, and landing on a read-only view each time — with the
//   preview gone — makes the pane it took twelve lines to build useless.
//
//   Unlike the page's, this screen has no nested routes and nothing to select:
//   a section has one element by construction. There is no editor context here
//   for that reason.
export const Content = ({
  match: {
    params: { siteId, sectionId },
  },
}) => (
  <EditorProvider>
    <Preview.Provider>
      <Mutation
        target={{ $ref: "/Public/Section/Update" }}
        mutation={SectionQL.edit}
        update={SectionQL.update({ sectionId })}
        // The editor stays where it is on save, so nothing remounts the route's
        // query and the section's own screen would otherwise keep showing the
        // content this just replaced. The cache eviction next door clears the
        // LISTINGS, so a create shows up in the list; it cannot make an already
        // mounted query hand back a row it is still holding.
        refetch={[{ query: SectionQL.get, variables: { siteId, sectionId } }]}
        onMutate={({ handler, state: { values } }) => {
          handler({ variables: { siteId, sectionId, input: values } });
        }}
        onCompleted={() => {}}
        onError={(error) => {
          Notify.error(error.toString());
        }}
      >
        {/* A stable reference, never an inline arrow: `Mutation` renders what
            it is handed, so a fresh function here is a new component type on
            every render and remounts the whole editor under it — which throws
            away whatever is half-typed. Same mechanism as P144. */}
        {MutationHandler}
      </Mutation>
    </Preview.Provider>
  </EditorProvider>
);

// The three code fields. Wide, because markup in an inline field is unreadable.
const CodeTab = ({ target, state, schema, mutate }) => (
  <Form
    schema={schema}
    target={target}
    widgets={widgets}
    values={state.values}
    onChange={state.setValues}
    onSubmit={mutate}
    config={CODE_FIELDS}
  />
);

// The two halves of the templating layer, and the reason a section is worth
// having at all: Types declares the slots, Values fills them. Neither is an
// ordinary schema field — `values` has no fixed shape, and the schema for it is
// compiled from `types` at render time — so both are bespoke components reading
// and writing the same form state as every other tab.
const DataTab = ({ state }) => {
  const { values, setValues } = state;

  return (
    <ContentTabs
      tabs={[
        {
          label: "Values",
          content: (
            <ValuesPane
              context="section"
              types={values.types}
              values={values.values}
              widgets={widgets}
              // The generated schema references /Image and friends by $ref, and
              // a section's own types may too. Passing the shared fragments in
              // means a param can use them without types-values knowing they
              // exist.
              utils={SectionPublicSchema}
              onChange={(mods) => setValues({ ...values, values: mods })}
            />
          ),
        },
        {
          label: "Types",
          content: (
            <TypesForm
              help={typesHelp("section")}
              schema={TypesSchema}
              paramTypes={paramTypes}
              widgets={widgets}
              values={{ types: values.types }}
              onChange={({ types }) => setValues({ ...values, types })}
            />
          ),
        },
      ]}
    />
  );
};

// What the section is wrapped in and how it is found. Small, and last, because
// it is the tab you open least.
const DetailsTab = ({ target, state, schema, mutate }) => (
  <Form
    schema={schema}
    target={target}
    widgets={widgets}
    values={state.values}
    onChange={state.setValues}
    onSubmit={mutate}
    config={DETAILS_FIELDS}
  />
);

const MutationHandler = (props) => {
  const {
    state,
    attributes: { siteId, sectionId },
  } = props;
  const navbar = useNavbarContext();
  const { setOpen } = useSidebarContext();

  // Which tab is open. Held here rather than passed in, because unlike the page
  // editor nothing above this needs to bring a tab forward — there is no
  // element route to open.
  const tabs = useContentState({ selected: 0 });

  // The editor wants the width. Collapsed on entry, restored on the way out —
  // the sidebar is a global, so leaving it collapsed would follow you to every
  // other screen.
  useEffect(() => {
    setOpen(false);
    return () => setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Which section, in the navbar, for the reason the page editor puts the page
  // there: the breadcrumb scrolls out of a two-pane screen and this does not.
  useEffect(() => {
    const Item = () => (
      <NavbarText className="ml-2">
        Section/<b>{sectionId}</b>
      </NavbarText>
    );

    navbar.push(Item);
    return () => navbar.pop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId]);

  const items = [
    { label: "Code", content: <CodeTab {...props} /> },
    { label: "Data", content: <DataTab {...props} /> },
    { label: "Details", content: <DetailsTab {...props} /> },
  ];

  return (
    <Layout
      left={
        <>
          <div className="float-right">
            <Nav tabs>
              <NavItem>
                {/* In the tab strip rather than under the form, because there
                    is no single form: three tabs write the same value object,
                    and a Save at the bottom of one of them would read as saving
                    only that one. */}
                <Button
                  enabledClass="nav-link text-success"
                  disabledClass="nav-link text-muted"
                >
                  Save
                </Button>
              </NavItem>
            </Nav>
          </div>

          <ContentTabs control={tabs} tabs={items} frame={TabFrame} />
        </>
      }
      right={<Frame siteId={siteId} state={state} />}
    />
  );
};

// Each tab scrolls inside the pane rather than growing it. Without this the
// left pane grows with the markup fields and the preview drifts off screen.
const TabFrame = ({ children }) => (
  <ContentAvailable className="container-fluid px-2">{children}</ContentAvailable>
);
